import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  setCurrentScreen,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
} from '../../store/orderSlice';
import Button from '../ui/Button';
import StartOverButton from '../ui/StartOverButton';
import CartImage from '../../data/PanaImages/CartImage.png';
import { CartItem } from '../../types';

const FALLBACK_IMG = 'https://images.spurcorp.com/D16EAE6E-A30A-4E5B-A97A-F654B1647789';
const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
const museoStack = '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

const CartScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { cart } = useSelector((state: RootState) => state.order);
  const [itemPendingRemoval, setItemPendingRemoval] = useState<CartItem | null>(null);

  const handleBackToMenu = () => {
    dispatch(setCurrentScreen('menu'));
  };

  const requestRemoveItem = (item: CartItem) => {
    setItemPendingRemoval(item);
  };

  const cancelRemoveItem = () => {
    setItemPendingRemoval(null);
  };

  const confirmRemoveItem = () => {
    if (!itemPendingRemoval) return;
    dispatch(removeFromCart(itemPendingRemoval.id));
    setItemPendingRemoval(null);
  };

  const handleUpdateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      const item = cart.find((cartItem) => cartItem.id === itemId);
      if (item) {
        requestRemoveItem(item);
        return;
      }
    } else {
      dispatch(updateCartItemQuantity({ id: itemId, quantity: newQuantity }));
      return;
    }

    dispatch(removeFromCart(itemId));
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    dispatch(setCurrentScreen('payment'));
  };

  const handleStartOver = () => {
    dispatch(clearCart());
    dispatch(setCurrentScreen('welcome'));
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const [randPart, centsPart] = cartTotal.toFixed(2).split('.');

  if (cart.length === 0) {
    return (
      <div className="h-screen flex flex-col bg-[#f2f1ec] items-center justify-center">
        <div className="text-center px-10">
          <h2 className="font-display font-black text-primary tracking-[2px] text-[56px] leading-[56px] uppercase mb-6">
            Your Order
          </h2>
          <p className="text-[20px] text-kiosk-text mb-8">Your cart is empty</p>
          <Button
            variant="primary"
            size="lg"
            onClick={handleBackToMenu}
            className="mx-auto"
            style={{ fontFamily: nexaRustStack, fontSize: '40px', fontWeight: 900 }}
          >
            Browse Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#f2f1ec] overflow-hidden">
      {/* Header */}
      <div className="px-10 pt-8 pb-12">
        <div className="flex items-center gap-12 justify-center">
          <div className="relative">
            <img src={CartImage} alt="Cart" className="w-20 h-20" />
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -left-2 text-white bg-secondary rounded-full px-2 py-0.5 text-base font-bold">
                {cartItemCount}
              </span>
            )}
          </div>
          <h1 className="font-display font-black text-[#185C53] tracking-[2px] text-[68px] leading-[68px] uppercase text-center">
            Your Order
          </h1>
        </div>
      </div>

      {/* Items list (scrollable) */}
      <div className="flex-1 min-h-0 overflow-y-auto px-10 pb-64 overscroll-contain will-change-scroll">
        <div>
          {cart.map((item) => {
            const [rands, cents] = item.totalPrice.toFixed(2).split('.');
            const displayUrl = item.menuItem.image && item.menuItem.image.startsWith('http')
              ? item.menuItem.image
              : FALLBACK_IMG;
            const optionsText = item.selectedOptions
              .map((o) => (o.price > 0 ? `${o.displayName} (R${o.price.toFixed(2)})` : o.displayName))
              .join(', ');
            return (
              <div
                key={item.id}
                className="flex items-start gap-6 py-6 border-b-2 border-gray-300 first:pt-0 last:pb-0 last:border-b-0"
              >
                <div className="w-[240px] aspect-[6/5] rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 shadow-md">
                  <img
                    src={displayUrl}
                    alt={item.menuItem.displayName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-black text-[36px] tracking-[0.5px] text-kiosk-text leading-snug uppercase text-left">
                    {item.menuItem.displayName}
                  </h3>
                  {optionsText && (
                    <p className="mt-2 text-[16px] text-kiosk-text-light text-left line-clamp-2">
                      {optionsText}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-6 ml-auto">
                  {/* Quantity above price + Price */}
                  <div className="min-w-[220px] flex flex-col items-end justify-between h-[200px]">
                    <div className="inline-flex items-center bg-transparent border-2 border-gray-300 rounded-xl px-3 py-1.5">
                      {item.quantity <= 1 ? (
                        <button
                          onClick={() => requestRemoveItem(item)}
                          className="px-2 text-[34px] leading-none font-black text-red-600"
                          aria-label="Remove item"
                        >
                          ×
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                          className="px-2 text-[34px] leading-none font-black text-gray-700"
                          aria-label="Decrease quantity"
                        >
                          –
                        </button>
                      )}
                      <span className="mx-4 font-display font-black text-[30px] leading-none text-kiosk-text select-none">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        className="px-2 text-[34px] leading-none font-black text-primary"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <div className="mt-3 text-right text-kiosk-text">
                      <div className="flex items-start justify-end">
                        <span className="font-display font-black text-[48px] leading-none">R {rands}</span>
                        <span className="ml-1 font-display font-black text-[26px] leading-none pt-1">{cents}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed left-0 right-0 bottom-0 z-50">
        <div className="bg-white rounded-t-2xl shadow-[0_-10px_30px_rgba(0,0,0,0.15)] px-10 pt-10 pb-6">
          <div className="relative flex items-center justify-center">
            <div className="font-display font-black text-[36px] text-kiosk-text">Total</div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-start text-kiosk-text">
              <span className="font-display font-black text-[56px] leading-none">R {randPart}</span>
              <span className="ml-1 font-display font-black text-[28px] leading-none pt-1">{centsPart}</span>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-6">
            <Button
              variant="outline"
              size="xl"
              onClick={handleBackToMenu}
              className="w-2/5 px-10 uppercase tracking-[1.2px] text-[28px] font-display !border-primary !text-primary"
            >
              Order More
            </Button>
            <Button
              variant="primary"
              size="xl"
              onClick={handleCheckout}
              disabled={cart.length === 0}
              className="flex-1 px-14 uppercase tracking-[1.2px] text-[28px] font-display rounded-tl-none rounded-tr-[18px] rounded-bl-[18px] rounded-br-none"
            >
              Complete Order
            </Button>
          </div>
        </div>
        <div className="w-full flex justify-center bg-white pt-2 pb-0">
          <StartOverButton onClick={handleStartOver} />
        </div>
      </div>

      {itemPendingRemoval && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center px-8"
          style={{ backgroundColor: '#f2f1ec' }}
        >
          <div className="flex flex-col items-center text-center gap-6">
            <h2
              className="font-display font-black uppercase tracking-[2px] text-[#00A294]"
              style={{ fontFamily: nexaRustStack, fontSize: '80px', lineHeight: '84px' }}
            >
              Remove Item
            </h2>
            <p
              className="text-[#333333]"
              style={{ fontFamily: museoStack, fontSize: '28px', lineHeight: '36px' }}
            >
              Are you sure you want to remove this item?
            </p>
            <p
              className="text-[#000000]"
              style={{ fontFamily: nexaRustStack, fontSize: '40px', lineHeight: '46px' }}
            >
              "{itemPendingRemoval.menuItem.displayName}"
            </p>
            <p
              className="text-[#333333]"
              style={{ fontFamily: museoStack, fontSize: '28px', lineHeight: '36px' }}
            >
              will be removed from your order.
            </p>
            <div className="mt-4 flex items-center gap-8">
              <button
                type="button"
                onClick={cancelRemoveItem}
                className="px-12 py-4 rounded-[18px] uppercase"
                style={{
                  fontFamily: nexaRustStack,
                  fontSize: '32px',
                  lineHeight: '36px',
                  fontWeight: 900,
                  backgroundColor: 'transparent',
                  color: '#E32B26',
                  border: '2px solid #E32B26',
                  minWidth: '220px',
                }}
              >
                Keep Item
              </button>
              <button
                type="button"
                onClick={confirmRemoveItem}
                className="px-12 py-4 rounded-tr-[18px] rounded-bl-[18px] rounded-tl-none rounded-br-none uppercase"
                style={{
                  fontFamily: nexaRustStack,
                  fontSize: '32px',
                  lineHeight: '36px',
                  fontWeight: 900,
                  backgroundColor: '#00A294',
                  color: '#FFFFFF',
                  border: '2px solid #00A294',
                  minWidth: '220px',
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartScreen;